import { useEffect } from "react";

export const MAIN_REGION_ID = "main-content";
export const NAV_REGION_ID = "main-navigation";

/**
 * Focusing an unrendered element silently does nothing — the sidebar is display:none below lg —
 * and asking the layout whether it is visible is what jsdom cannot answer. Trying it and reading
 * back where focus landed works the same in both.
 */
const focusRegion = (id: string): boolean => {
  const element = document.getElementById(id);
  if (!element) return false;
  element.focus();
  return document.activeElement === element;
};

/**
 * Whether this keypress asks to move between the sidebar and the work surface.
 *
 * F6 is the landmark-rotation convention browsers and screen readers already use, but on a Mac it
 * is a hardware control unless the reader has turned on standard function keys — so Ctrl+M carries
 * the same job on a key that always arrives. Ctrl+Shift+M is deliberately not used: Chrome takes it
 * for switching profiles.
 */
const isRegionToggle = (event: KeyboardEvent): boolean => {
  if (event.key === "F6") return !event.metaKey && !event.ctrlKey && !event.altKey;
  if (event.key.toLowerCase() === "m") {
    return event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
  }
  return false;
};

/**
 * Moves focus between the sidebar and the work surface.
 *
 * Unlike the "/" shortcut this deliberately fires while the reader is typing: neither binding
 * writes a character, and getting out of a field without tabbing past everything after it is the
 * point.
 */
export const useRegionToggle = (): void => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isRegionToggle(event)) return;
      const nav = document.getElementById(NAV_REGION_ID);
      const inNav = nav?.contains(event.target as Node) ?? false;
      const order = inNav ? [MAIN_REGION_ID, NAV_REGION_ID] : [NAV_REGION_ID, MAIN_REGION_ID];
      for (const id of order) {
        if (focusRegion(id)) {
          event.preventDefault();
          return;
        }
      }
    };
    // Capture, not bubble: the rich-text editor and the category tree both stop keydown from
    // propagating, and a shortcut for leaving them has to run before they can swallow it.
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);
};
